import { AddError } from '../addError/addError';
import { ActionFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarState } from '../../controller';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import {
  getStaffList,
  isSlotLocked,
  isSlotWithOpenWaitingList,
} from '../../../../utils/slotAvailability/slotAvailability';
import { checkForPricingPlanError } from '../../../../utils/pricingPlans/pricingPlans';
import { OnServiceVariantsOptionSelected } from '../onServiceVariantsOptionSelected/onServiceVariantsOptionSelected';
import { isServiceVariantWithStaff } from '../../../../utils/dynamicPricing/dynamicPricing';
import { Preference, TriggeredByOptions } from '../../../../types/types';
import { bookingsCalendarBookingDetailsLoad } from '@wix/bi-logger-wixboost-ugc/v2';
import { isSelectedTimeSlotWithWaitingListIndication } from '../../../../utils/timeSlots/timeSlots';
import { DO_NOT_CARE_STAFF } from '../../../../constants/constants';
import { canAutoAssignStaff } from '../../../../utils/serviceUtils/serviceUtils';
import { TimeSlot } from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { initializeSelectedVariantsOptions } from '../../../../utils/initializeSelectedVariantsOptions/initializeSelectedVariantsOptions';
import { initializeSelectedBookingPreferences } from '../../../../utils/initializeSelectedBookingPreferences/initializeSelectedBookingPreferences';

export type SetSelectedTime = (
  selectedTime: string | undefined,
  triggeredBy: TriggeredByOptions,
) => void;

export function createSetSelectedTimeAction(
  {
    getControllerState,
    context,
  }: ActionFactoryParams<CalendarState, CalendarContext>,
  addError: AddError,
  onServiceVariantsOptionSelected: OnServiceVariantsOptionSelected,
): SetSelectedTime {
  return (selectedTime: string | undefined, triggeredBy) => {
    const [state, setState] = getControllerState();
    const {
      availableSlots,
      purchasedPricingPlans,
      rescheduleBookingDetails,
      serviceVariantsMap,
      servicesInView,
    } = state;
    const { biLogger, isDateAndTimeViewMode, calendarSelections } = context;
    const service = servicesInView[0];
    const serviceVariants = serviceVariantsMap[service.id!];

    const selectedVariantsOptions = !isDateAndTimeViewMode
      ? initializeSelectedVariantsOptions({
          calendarSelections,
          servicesInView,
          serviceVariantsMap,
        })
      : [];

    setState({
      calendarErrors: [],
      selectedVariantsOptions,
    });

    let selectableSlots;
    if (selectedTime) {
      selectableSlots = availableSlots?.timeSlots?.filter((availableSlot) => {
        return (
          availableSlot.localStartDate === selectedTime &&
          (availableSlot.bookable ||
            isSlotWithOpenWaitingList(availableSlot) ||
            isSlotLocked(availableSlot))
        );
      });

      const isRescheduleFlow = !!rescheduleBookingDetails;
      const pricingPlanError = checkForPricingPlanError({
        purchasedPricingPlans,
        isRescheduleFlow,
        selectedTime,
      });
      if (pricingPlanError) {
        addError(pricingPlanError);
      }

      if (
        !isDateAndTimeViewMode &&
        isServiceVariantWithStaff(serviceVariants) &&
        selectableSlots &&
        selectableSlots.length > 0
      ) {
        const firstSlotStaffId = getStaffList(selectableSlots[0])[0]?.id!;

        const isSlotContainsOnlyFirstStaffId = (slot: TimeSlot) => {
          const staffListSet = new Set(
            getStaffList(slot).map((resource) => resource.id),
          );
          return staffListSet.size === 1 && staffListSet.has(firstSlotStaffId);
        };

        if (selectableSlots.every(isSlotContainsOnlyFirstStaffId)) {
          onServiceVariantsOptionSelected({
            optionId: serviceVariants?.options!.values![0].id!,
            choiceId: firstSlotStaffId,
            numberOfParticipants: 1,
            isMultipleChoices: false,
          });
        }
      }

      const isWaitingListFlow = isSelectedTimeSlotWithWaitingListIndication(
        availableSlots?.timeSlots,
        selectedTime,
      );
      const waitListProperty = isWaitingListFlow ? { waitlist: true } : {};

      void biLogger.report(
        bookingsCalendarBookingDetailsLoad({
          triggeredBy,
          selectedSlot: selectedTime,
          properties: JSON.stringify({
            ...waitListProperty,
          }),
        }),
      );
    }

    const isAutoAssignStaffAllowed = canAutoAssignStaff({
      state,
      context,
      selectableSlotsAtSelectedTime: selectableSlots ?? [],
    });

    const selectedBookingPreferences = !isDateAndTimeViewMode
      ? initializeSelectedBookingPreferences({
          calendarSelections,
          servicesInView,
        })
      : [];

    if (isAutoAssignStaffAllowed) {
      selectedBookingPreferences.push({
        key: Preference.STAFF_MEMBER,
        value: DO_NOT_CARE_STAFF,
      });
    }

    setState({
      selectedBookingPreferences,
      selectableSlotsAtSelectedTime: selectableSlots ?? [],
      selectedTime,
    });
  };
}
